import React, { useState, useMemo, useEffect } from "react";
import {
  IconSearch,
  IconPlus,
  IconChevronLeft,
  IconChevronRight,
  IconX,
  IconTrash,
  IconEdit,
  IconFileText,
} from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchHrDesignations,
  fetchHrDepartments,
  createHrDesignation,
  updateHrDesignation,
  deleteHrDesignation,
  fetchHrCompany,
  fetchHrBranches,
  HrDesignation,
  HrDepartment,
  HrBranch,
} from "../../../apis/hrApi";
import toast from "react-hot-toast";

const SectionBlock = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm p-4 transition-all duration-300 space-y-4 relative">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3 relative z-10">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, placeholder, type = "text", className = "", error, required, ...rest }, ref) => {
    return (
      <div className={`w-full space-y-1 ${className}`}>
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            ref={ref}
            type={type}
            placeholder={placeholder}
            {...rest}
            className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
          />
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    );
  }
);
InputField.displayName = "InputField";

const SelectInput = ({
  label,
  value,
  setValue,
  selectOptions = [],
  className = "",
  required,
  disabled,
}: {
  label: string;
  value?: string | number;
  setValue?: (val: string) => void;
  selectOptions?: { value: string | number; text: string }[] | false;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}) => {
  return (
    <div className={`space-y-1 w-full ${className}`}>
      <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative flex items-center w-full">
        <select
          value={value}
          onChange={(e) => setValue && setValue(e.target.value)}
          disabled={disabled}
          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed cursor-pointer"
        >
          <option value="">Select</option>
          {selectOptions &&
            selectOptions.map((opt, i) => (
              <option key={i} value={opt.value}>
                {opt.text}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
};

interface OrgNode {
  name: string;
  children?: OrgNode[];
}

function TreeNode({ node, isRoot = false }: { node: OrgNode; isRoot?: boolean }) {
  const hasChildren = node.children && node.children.length > 0;
  
  const cardStyle = isRoot
    ? "bg-[#212121] text-white border border-[#212121] px-6 py-3 rounded-xl font-extrabold uppercase tracking-wider shadow-md"
    : "bg-white text-[#212121] border border-gray-200 border-l-4 border-l-[#FBD037] px-5 py-2.5 rounded-xl font-semibold shadow-xs hover:border-gray-300 hover:shadow-sm";

  return (
    <li>
      <div className={`flex items-center justify-center text-xs whitespace-nowrap transition-all duration-300 hover:scale-105 cursor-pointer ${cardStyle}`}>
        <span>{node.name}</span>
      </div>
      {hasChildren && (
        <ul>
          {node.children!.map((child, idx) => (
            <TreeNode key={idx} node={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function Designation() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<"table" | "hierarchy">("table");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [sidebarMode, setSidebarMode] = useState<"ADD" | "EDIT" | "DELETE" | null>(null);

  // Form States for Add
  const [newName, setNewName] = useState("");
  const [newParent, setNewParent] = useState("");
  const [newDept, setNewDept] = useState("");

  // Form States for Edit/Delete
  const [selectedDes, setSelectedDes] = useState<HrDesignation | null>(null);
  const [editName, setEditName] = useState("");
  const [editParent, setEditParent] = useState("");
  const [editDept, setEditDept] = useState("");
  const [editStatus, setEditStatus] = useState("ACTIVE");

  const [companyId, setCompanyId] = useState("");
  const [branches, setBranches] = useState<HrBranch[]>([]);
  const [addBranchId, setAddBranchId] = useState("");

  useEffect(() => {
    fetchHrCompany().then(res => {
      if (res.success && res.data) {
        setCompanyId(res.data.id || res.data._id || "");
      }
    }).catch(console.error);
    fetchHrBranches(1, 100).then(res => {
      if (res.success && res.data?.branches) {
        setBranches(res.data.branches);
        if (res.data.branches.length > 0) {
          setAddBranchId(res.data.branches[0].id);
        }
      }
    }).catch(console.error);
  }, []);

  // Debounce search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // Fetch designations
  const { data, isLoading } = useQuery({
    queryKey: ["designations", debouncedSearch],
    queryFn: () => fetchHrDesignations(1, 50, debouncedSearch),
  });

  const designations = data?.data?.designations || [];

  // Fetch departments for dropdown
  const { data: deptData } = useQuery({
    queryKey: ["departments"],
    queryFn: () => fetchHrDepartments(1, 100, ""),
  });
  
  const departments = deptData?.data?.departments || [];

  // Mutations
  const createMutation = useMutation({
    mutationFn: createHrDesignation,
    onSuccess: () => {
      toast.success("Designation created successfully!");
      queryClient.invalidateQueries({ queryKey: ["designations"] });
      setSidebarMode(null);
      setNewName("");
      setNewParent("");
      setNewDept("");
    },
    onError: (err: any) => toast.error(err?.message || "Failed to create designation"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) => updateHrDesignation(id, payload),
    onSuccess: () => {
      toast.success("Designation updated successfully!");
      queryClient.invalidateQueries({ queryKey: ["designations"] });
      setSidebarMode(null);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to update designation"),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteHrDesignation,
    onSuccess: () => {
      toast.success("Designation deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["designations"] });
      setSidebarMode(null);
    },
    onError: (err: any) => toast.error(err?.message || "Failed to delete designation"),
  });

  // Handlers
  const handleEditClick = (des: HrDesignation) => {
    setSelectedDes(des);
    setEditName(des.designationName);
    setEditParent(des.parentDesignationId || "");
    setEditDept(des.departmentId || "");
    setEditStatus(des.status || "ACTIVE");
    setSidebarMode("EDIT");
  };

  const handleDeleteClick = (des: HrDesignation) => {
    setSelectedDes(des);
    setSidebarMode("DELETE");
  };

  const handleAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName || !newDept) return;
    if (!addBranchId) { toast.error("Please select a branch"); return; }
    createMutation.mutate({
      branchId: addBranchId,
      designationName: newName,
      parentDesignationId: newParent || null,
      departmentId: newDept,
    });
  };

  const handleEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editName || !editDept || !selectedDes) return;
    updateMutation.mutate({
      id: selectedDes.id,
      payload: {
        designationName: editName,
        parentDesignationId: editParent || null,
        departmentId: editDept,
        status: editStatus,
      },
    });
  };

  const columns = [
    {
      label: (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
      render: () => (
        <div className="flex items-center justify-center">
          <label className="relative flex items-center justify-center cursor-pointer h-4 w-4">
            <input
              type="checkbox"
              className="peer h-4 w-4 cursor-pointer appearance-none rounded border border-slate-300 checked:border-[#FBD037] bg-white checked:bg-[#FBD037] transition-all"
            />
            <span className="absolute text-[10px] font-bold text-[#131327] peer-checked:opacity-100 opacity-0 pointer-events-none">
              ✓
            </span>
          </label>
        </div>
      ),
    },
    { label:"DESIGNATION ID", render:(s:HrDesignation)=><span className="font-semibold text-[#212121]">{s.designationId}</span> },
    { label:"DESIGNATION NAME", render:(s:HrDesignation)=><span className="font-semibold text-[#212121] text-sm">{s.designationName}</span> },
    { label:"PARENT DESIGNATION", render:(s:HrDesignation)=>{
        const parent = designations.find((d: HrDesignation) => d.id === s.parentDesignationId);
        return <span className="text-sm text-gray-700">{parent ? parent.designationName : "—"}</span>;
      } 
    },
    { label:"DEPARTMENT", render:(s:HrDesignation)=>{
        const dept = departments.find((d: HrDepartment) => d.id === s.departmentId);
        return <span className="text-sm text-gray-700">{dept ? dept.departmentName : "—"}</span>;
      }
    },
    { label:"STATUS", render:(s:HrDesignation)=><span className={`text-xs px-2 py-1 rounded-full font-semibold ${s.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{s.status}</span> },
    { label:"ACTIONS", render:(s:HrDesignation)=>(
      <div className="flex justify-center items-center gap-2">
        <button
          onClick={() => handleEditClick(s)}
          className="p-1.5 border border-[#131327]/10 rounded-full bg-[#F7F6F2] hover:bg-[#e8e7e3] text-gray-600 hover:text-[#212121] transition-colors cursor-pointer"
          title="Edit"
        >
          <IconEdit size={13} stroke={2.5} />
        </button>
        <button
          onClick={() => handleDeleteClick(s)}
          className="p-1.5 border border-red-200/55 rounded-full bg-[#FCF8F8] hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors cursor-pointer"
          title="Delete"
        >
          <IconTrash size={13} stroke={2.5} />
        </button>
      </div>
    )},
  ];

  const buildTree = (): OrgNode => {
    const rootDes = designations.filter((d: HrDesignation) => !d.parentDesignationId);
    const getChildren = (parentId: string): OrgNode[] => {
      return designations
        .filter((d: HrDesignation) => d.parentDesignationId === parentId)
        .map((d: HrDesignation) => ({
          name: d.designationName,
          children: getChildren(d.id),
        }));
    };
    return {
      name: "Company Root",
      children: rootDes.map((d: HrDesignation) => ({
        name: d.designationName,
        children: getChildren(d.id),
      }))
    };
  };

  const designationTree = useMemo(() => buildTree(), [designations]);

  return (
    <div className="w-full mx-auto mt-2 animate-in fade-in duration-300">
      {/* Connector line styles for Org Chart */}
      <style dangerouslySetInnerHTML={{ __html: `
        .org-tree {
          display: flex;
          justify-content: center;
          align-items: flex-start;
          padding: 40px 20px;
          overflow-x: auto;
          min-width: max-content;
        }
        .org-tree ul {
          padding-top: 24px;
          position: relative;
          transition: all 0.5s;
          display: flex;
          justify-content: center;
        }
        .org-tree li {
          text-align: center;
          list-style-type: none;
          position: relative;
          padding: 24px 8px 0 8px;
          transition: all 0.5s;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .org-tree li::before, .org-tree li::after {
          content: '';
          position: absolute;
          top: 0;
          right: 50%;
          border-top: 2px solid #CBD5E1;
          width: 50%;
          height: 24px;
        }
        .org-tree li::after {
          right: auto;
          left: 50%;
          border-left: 2px solid #CBD5E1;
        }
        .org-tree li:only-child::before {
          display: none;
        }
        .org-tree li:only-child::after {
          border-top: 0;
          border-left: 2px solid #CBD5E1;
          left: 50%;
          transform: translateX(-50%);
          height: 24px;
        }
        .org-tree li:only-child {
          padding-top: 24px;
        }
        .org-tree li:first-child::before, .org-tree li:last-child::after {
          border: 0 none;
        }
        .org-tree li:last-child::before {
          border-right: 2px solid #CBD5E1;
          border-radius: 0 8px 0 0;
        }
        .org-tree li:first-child::after {
          border-radius: 8px 0 0 0;
        }
        .org-tree ul ul::before {
          content: '';
          position: absolute;
          top: 0;
          left: 50%;
          border-left: 2px solid #CBD5E1;
          width: 0;
          height: 24px;
          transform: translateX(-50%);
        }
        .org-tree > ul > li {
          padding-top: 0;
        }
        .org-tree > ul > li::before, .org-tree > ul > li::after {
          display: none !important;
        }
      `}} />

      {/* Header and Switcher */}
      <div className="flex items-center justify-end mb-5 px-2">
        <div className="flex items-center gap-4">
          <div className="flex bg-[#E4E7EB] rounded-xl p-1 font-sans text-xs font-bold shadow-inner">
            <button
              onClick={() => setActiveTab("table")}
              className={`px-4 py-1.5 rounded-lg transition-all ${
                activeTab === "table" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Table View
            </button>
            <button
              onClick={() => setActiveTab("hierarchy")}
              className={`px-4 py-1.5 rounded-lg transition-all ${
                activeTab === "hierarchy" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Hierarchy
            </button>
          </div>
        </div>
      </div>

      {activeTab === "table" ? (
        <div className="grid grid-cols-12 gap-6 bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300 rounded-b-3xl rounded-tr-3xl w-full">
          <div className={sidebarMode ? "col-span-12 xl:col-span-7" : "col-span-12"}>
            <div className="bg-white rounded-3xl border border-gray-200/50 shadow-sm overflow-hidden">
              {/* Toolbar Controls */}
              <div className="px-6 py-1.5 flex flex-col lg:flex-row lg:items-center justify-end gap-4 border-b border-slate-50">
                <div className="flex items-center flex-wrap gap-2.5">
                  <select className="border border-slate-200 rounded-lg px-2 py-1 text-sm bg-white outline-none focus:ring-1 focus:ring-[#FBD037] h-8">
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                  <span className="text-sm text-gray-500 mr-2">entries</span>

                  <div className="flex items-center gap-2 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <IconSearch size={16} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 pr-4 h-8 rounded-lg border border-slate-200 bg-white text-sm outline-none focus:ring-1 focus:ring-[#FBD037] transition"
                    />
                  </div>

                  {/* Add button inside toolbar */}
                  <button
                    type="button"
                    onClick={() => {
                      setNewName("");
                      setNewParent("");
                      setNewDept("");
                      setSidebarMode("ADD");
                    }}
                    className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#FBD037] hover:bg-[#FACC15] active:scale-95 transition shadow-sm hover:shadow-md cursor-pointer"
                    title="Add Designation"
                  >
                    <IconPlus stroke={2.5} size={16} className="text-[#212121]" />
                  </button>
                </div>
              </div>

              <div className="pb-4">
                {isLoading ? (
                  <div className="p-8 text-center text-gray-500">Loading designations...</div>
                ) : designations.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No designations found.</div>
                ) : (
                  <ListView
                    columns={columns}
                    data={designations}
                    trClassName="hover:bg-[#FBD037]/10 group transition-colors duration-150 border-b border-gray-100 cursor-pointer"
                    mainDivClassName="bg-[#F7F6F2] rounded-3xl"
                    mailDivClassName="custom-scrollbar bg-[#FCFDF8] rounded-3xl shadow-inner max-h-[75vh]"
                    scrollbarClassName="custom-scrollbar"
                    thClassName="text-[11px] font-semibold tracking-wider uppercase select-none text-[#131327]/70"
                  />
                )}
                
                <div className="py-4 flex justify-center">
                  <div className="flex items-center gap-1">
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
                      disabled
                    >
                      <IconChevronLeft size={14} stroke={2.5} />
                    </button>
                    <button className="w-8 h-8 flex items-center justify-center rounded-lg border text-xs font-bold bg-[#FBD037] border-[#FBD037] text-[#131327] transition shadow-sm">
                      1
                    </button>
                    <button
                      className="flex items-center justify-center w-8 h-8 rounded-lg border border-slate-200 bg-white text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition shadow-sm cursor-pointer"
                      disabled
                    >
                      <IconChevronRight size={14} stroke={2.5} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Sidebar Right Panel ────────────────────────────────────────── */}
          {sidebarMode !== null && (
            <>
              {/* Backdrop on mobile */}
              <div
                className="fixed inset-0 bg-black/40 z-30 xl:hidden"
                onClick={() => setSidebarMode(null)}
              />
              <div className="col-span-12 xl:col-span-5 space-y-4 mt-2 xl:static fixed inset-y-0 right-0 z-[35] w-full sm:w-[450px] xl:w-auto p-4 pt-[76px] sm:pt-[96px] xl:p-0 shadow-2xl xl:shadow-none bg-white xl:bg-transparent overflow-y-auto xl:overflow-visible flex flex-col justify-start">
                <div className="flex flex-col bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden">

                  {/* Mode: ADD */}
                  {sidebarMode === "ADD" && (
                    <>
                      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
                        <h2 className="text-lg font-bold text-[#212121]">Add New Designation</h2>
                        <div className="flex items-center gap-2">
                          <button
                            type="submit"
                            form="addDesForm"
                            disabled={createMutation.isPending}
                            className="px-4 py-2 bg-[#FBD037] text-[#212121] text-sm font-semibold rounded-lg hover:bg-[#f5c71a] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer border-none shadow-sm"
                          >
                            {createMutation.isPending ? "Adding..." : "Add"}
                          </button>
                          <button type="button" onClick={() => setSidebarMode(null)} className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer">
                            <IconX size={20} />
                          </button>
                        </div>
                      </div>
                      <form id="addDesForm" onSubmit={handleAddSubmit} className="p-5 overflow-y-auto max-h-[calc(100vh-160px)]">
                        <SectionBlock title="Designation Details" icon={IconFileText}>
                          <div className="space-y-4">
                            <SelectInput
                              label="Branch"
                              required
                              value={addBranchId}
                              setValue={setAddBranchId}
                              selectOptions={branches.map(b => ({
                                value: b.id,
                                text: b.branchName
                              }))}
                            />
                            <InputField
                              label="Designation Name"
                              required
                              placeholder="Ex. CFO/HR."
                              value={newName}
                              onChange={e => setNewName(e.target.value)}
                            />
                            <SelectInput
                              label="Parent Designation"
                              value={newParent}
                              setValue={setNewParent}
                              selectOptions={designations.map((d: HrDesignation) => ({
                                value: d.id,
                                text: d.designationName
                              }))}
                            />
                            <SelectInput
                              label="Department"
                              required
                              value={newDept}
                              setValue={setNewDept}
                              selectOptions={departments.map((dept: HrDepartment) => ({
                                value: dept.id,
                                text: dept.departmentName
                              }))}
                            />
                          </div>
                        </SectionBlock>
                      </form>
                    </>
                  )}

                  {/* Mode: EDIT */}
                  {sidebarMode === "EDIT" && selectedDes && (
                    <>
                      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
                        <h2 className="text-lg font-bold text-[#212121]">Edit Designation</h2>
                        <div className="flex items-center gap-2">
                          <button
                            type="submit"
                            form="editDesForm"
                            disabled={updateMutation.isPending}
                            className="px-4 py-2 bg-[#FBD037] text-[#212121] text-sm font-semibold rounded-lg hover:bg-[#f5c71a] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer border-none shadow-sm"
                          >
                            {updateMutation.isPending ? "Saving..." : "Save"}
                          </button>
                          <button type="button" onClick={() => setSidebarMode(null)} className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer">
                            <IconX size={20} />
                          </button>
                        </div>
                      </div>
                      <form id="editDesForm" onSubmit={handleEditSubmit} className="p-5 overflow-y-auto max-h-[calc(100vh-160px)]">
                        <SectionBlock title="Designation Details" icon={IconFileText}>
                          <div className="space-y-4">
                            <InputField
                              label="Designation Name"
                              required
                              placeholder="Ex. CFO/HR."
                              value={editName}
                              onChange={e => setEditName(e.target.value)}
                            />
                            <SelectInput
                              label="Parent Designation"
                              value={editParent}
                              setValue={setEditParent}
                              selectOptions={designations.filter((d: HrDesignation) => d.id !== selectedDes.id).map((d: HrDesignation) => ({
                                value: d.id,
                                text: d.designationName
                              }))}
                            />
                            <SelectInput
                              label="Department"
                              required
                              value={editDept}
                              setValue={setEditDept}
                              selectOptions={departments.map((dept: HrDepartment) => ({
                                value: dept.id,
                                text: dept.departmentName
                              }))}
                            />
                            <SelectInput
                              label="Status"
                              value={editStatus}
                              setValue={setEditStatus}
                              selectOptions={[
                                { value: "ACTIVE", text: "ACTIVE" },
                                { value: "INACTIVE", text: "INACTIVE" }
                              ]}
                            />
                          </div>
                        </SectionBlock>
                      </form>
                    </>
                  )}

                  {/* Mode: DELETE */}
                  {sidebarMode === "DELETE" && selectedDes && (
                    <>
                      <div className="flex items-center justify-between px-5 py-3.5 shrink-0 border-b border-gray-100 bg-white">
                        <h2 className="text-lg font-bold text-[#212121]">Delete Designation</h2>
                        <button type="button" onClick={() => setSidebarMode(null)} className="p-2 rounded-lg border border-[#D2D2D2] hover:bg-gray-100 transition-colors text-slate-700 cursor-pointer">
                          <IconX size={20} />
                        </button>
                      </div>
                      <div className="p-6 text-center">
                        <div className="w-14 h-14 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <IconTrash size={28} />
                        </div>
                        <h3 className="text-base font-bold text-[#212121] mb-2">Are you sure?</h3>
                        <p className="text-xs text-gray-500 mb-6">
                          Do you really want to delete designation <strong>{selectedDes.designationName}</strong>? This action cannot be undone.
                        </p>
                        <div className="flex items-center justify-center gap-3">
                          <button type="button" onClick={() => setSidebarMode(null)} className="px-4 py-2 border border-gray-300 rounded-xl text-xs font-bold text-gray-700 bg-white hover:bg-gray-50 transition flex-1 cursor-pointer">Cancel</button>
                          <button onClick={() => deleteMutation.mutate(selectedDes.id)} disabled={deleteMutation.isPending} className="px-4 py-2 bg-[#FBD037] text-[#212121] hover:bg-[#e2bd30] text-xs font-bold rounded-xl transition cursor-pointer border-none flex items-center justify-center min-w-max shadow-sm hover:shadow-md duration-150 hover:-translate-y-0.5 active:translate-y-0 flex-1">
                            {deleteMutation.isPending ? "Deleting..." : "Delete"}
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        /* Hierarchy View Chart */
        <div className="w-full overflow-x-auto bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl rounded-b-3xl rounded-tr-3xl border border-white/20 shadow-sm select-none mb-10">
          <div className="org-tree min-w-max">
            <ul>
              <TreeNode node={designationTree} isRoot={true} />
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
